/**
 * Formatação pt-BR compartilhada pelos componentes do Radar.
 *
 * Os campos de data chegam em UTC ISO e são exibidos no fuso local do usuário.
 * Os valores monetários chegam com 4 casas decimais (ex.: `18900.0000`).
 */

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const dataHora = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
const horaCurta = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const diaLegivel = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'long',
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});
const quantidadeFormato = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 });

export const SEM_VALOR = '—';

export function paraData(valor: string | null | undefined): Date | null {
  if (!valor) return null;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}

/** Data e hora no fuso local, ou um texto honesto quando não há valor. */
export function formatarDataHora(valor: string | null | undefined, vazio = 'não informada'): string {
  const data = paraData(valor);
  return data ? dataHora.format(data) : vazio;
}

export function formatarHora(valor: string | null | undefined): string {
  const data = paraData(valor);
  return data ? horaCurta.format(data) : SEM_VALOR;
}

export function formatarDiaLegivel(valor: string | null | undefined): string {
  const data = paraData(valor);
  return data ? diaLegivel.format(data) : 'Data não informada';
}

/** Chave de agrupamento por dia local (AAAA-MM-DD), usada no `dateTime`. */
export function chaveDoDia(valor: string | null | undefined): string {
  const data = paraData(valor);
  if (!data) return 'sem-data';
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

/** Moeda em BRL. Aceita string com 4 casas decimais, como vem do portal. */
export function formatarMoeda(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return SEM_VALOR;
  const numero = typeof valor === 'number' ? valor : Number(String(valor).replace(',', '.'));
  return Number.isFinite(numero) ? moeda.format(numero) : SEM_VALOR;
}

export function formatarQuantidade(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return SEM_VALOR;
  const numero = typeof valor === 'number' ? valor : Number(String(valor).replace(',', '.'));
  return Number.isFinite(numero) ? quantidadeFormato.format(numero) : String(valor);
}

export function textoOuVazio(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined) return SEM_VALOR;
  const texto = String(valor).trim();
  return texto || SEM_VALOR;
}
